import { CSSObject, MantineTheme, MantineThemeOverride } from '@mantine/core'

const Inputs = [
  'Autocomplete',
  'ColorInput',
  'FileInput',
  'Input',
  'JsonInput',
  'MultiSelect',
  'NativeSelect',
  'NumberInput',
  'PasswordInput',
  'Select',
  'Textarea',
  'TextInput'
].reduce(
  (object, item) => ({
    ...object,
    [item]: {
      defaultProps: {
        size: 'md',
        radius: 'md'
      }
    }
  }),
  {}
)

const hover = { '&:hover': { cursor: 'pointer' } }

const defaultMantineTheme = (theme: MantineThemeOverride) => ({
  fontFamily: 'Open Sans, Helvetica, Verdana, sans-serif',
  fontFamilyMonospace: 'Monaco, Courier, monospace',
  black: '#333',
  radius: {
    ...theme.radius,
    md: 10
  },
  headings: {
    fontFamily: 'Heebo, sans-serif',
    fontWeight: 700
  },
  components: {
    ActionIcon: { defaultProps: { size: 'lg', radius: 'xl' } },
    Accordion: {
      defaultProps: { radius: 'md' },
      styles: (theme: MantineTheme) => ({
        item: { backgroundColor: `${theme.white} !important` }
      })
    },
    Alert: {
      styles: (theme: MantineTheme) => ({
        root: { borderRadius: theme.radius.md }
      })
    },
    Badge: {
      defaultProps: { radius: 'sm' },
      styles: {
        root: { textTransform: 'capitalize' } as CSSObject
      }
    },
    Button: {
      defaultProps: {
        uppercase: true,
        loaderProps: { variant: 'oval' }
      },
      styles: (theme: MantineTheme) => ({
        root: {
          borderRadius: theme.radius.md,
          overflow: 'hidden',
          '&[data-disabled]': {
            backgroundColor: theme.colors.gray[3]
          },
          '&[data-loading]': {
            color: theme.colors.gray[7],
            backgroundColor: theme.colors.gray[3],
            svg: { stroke: theme.colors.gray[7] }
          }
        },
        label: { overflow: 'hidden' }
      })
    },
    Card: {
      styles: (theme: MantineTheme) => ({
        root: {
          boxShadow: theme.shadows.xs,
          borderRadius: theme.radius.md
        }
      })
    },
    Drawer: {
      defaultProps: {
        overlayColor: theme.colors?.gray?.[3],
        overlayOpacity: 0.8,
        overlayBlur: 3
      },
      styles: () => ({
        drawer: {
          borderRadius: 0
        }
      })
    },
    LoadingOverlay: {
      defaultProps: {
        loaderProps: { variant: 'dots' },
        overlayColor: theme.colors?.gray?.[0],
        overlayOpacity: 0.8,
        overlayBlur: 3
      }
    },
    Modal: {
      defaultProps: {
        overlayColor: theme.colors?.gray?.[3],
        overlayOpacity: 0.8,
        overlayBlur: 3
      }
    },
    Text: { defaultProps: { color: theme.colors?.gray?.[7] } },
    Title: { defaultProps: { color: theme.colors?.gray?.[7] } },
    InputWrapper: {
      inputWrapperOrder: ['label', 'input', 'error', 'description'],
      styles: (theme: MantineTheme) => ({
        label: { fontSize: theme.fontSizes.sm },
        description: {
          fontSize: theme.fontSizes.xs,
          marginTop: 4,
          marginLeft: 4
        },
        error: {
          fontSize: theme.fontSizes.xs,
          marginTop: 4,
          marginLeft: 4
        }
      })
    },
    Checkbox: {
      styles: () => ({
        input: hover,
        label: hover,
        labelWrapper: { display: 'flex', alignItems: 'flex-start', ...hover }
      })
    },
    Paper: {
      styles: (theme: MantineTheme) => ({
        root: {
          boxShadow: theme.shadows.xs,
          borderRadius: theme.radius.md
        }
      })
    },
    Radio: {
      styles: () => ({
        radio: hover,
        label: hover,
        labelWrapper: { display: 'flex', alignItems: 'flex-start', ...hover }
      })
    },
    Stepper: {
      styles: (theme: MantineTheme) => ({
        stepIcon: { backgroundColor: theme.white }
      })
    },
    Switch: {
      styles: () => ({
        track: hover,
        trackLabel: hover,
        label: hover,
        labelWrapper: { display: 'flex', alignItems: 'flex-start', ...hover }
      })
    },
    Table: {
      styles: (theme: MantineTheme) => ({
        root: {
          backgroundColor: theme.white,
          borderRadius: theme.radius.md,
          overflow: 'hidden'
        }
      })
    },
    Tabs: {
      styles: (theme: MantineTheme) => ({
        tab: {
          backgroundColor: theme.white,
          borderBottom: `2px solid ${theme.colors.gray[3]}`
        }
      })
    },
    ...Inputs
  }
})

export default defaultMantineTheme
